import { useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import UnitCard from '@components/UnitCard';
import ConfigurePanel from '@components/ConfigurePanel';
import NewCustomerButton from '@components/NewCustomerButton';
import { useT } from '@lib/i18n';
import { useSessionStore } from '@stores/sessionStore';
import { useCatalogStore } from '@stores/catalogStore';
import type { Unit } from '@lib/types';

export default function Step4Match() {
  const t = useT();
  const navigate = useNavigate();
  const language = useSessionStore((s) => s.language);
  const filteredUnits = useCatalogStore((s) => s.filteredUnits);
  const selectedUnit = useSessionStore((s) => s.selectedUnit);
  const selectUnit = useSessionStore((s) => s.selectUnit);

  // If user lands on /match without going through Step 3, send them back.
  useEffect(() => {
    if (filteredUnits.length === 0) {
      navigate('/goal', { replace: true });
    }
  }, [filteredUnits.length, navigate]);

  const isAr = language === 'ar';

  const selectedUnitObject = useMemo(
    () => filteredUnits.find((u) => u.unitCode === selectedUnit) ?? null,
    [filteredUnits, selectedUnit]
  );

  const configureRef = useRef<HTMLDivElement | null>(null);

  // Smooth-scroll the configure panel into view when a unit is freshly selected.
  useEffect(() => {
    if (!selectedUnitObject) return;
    const id = window.setTimeout(() => {
      configureRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 60);
    return () => window.clearTimeout(id);
  }, [selectedUnitObject]);

  const handleSelect = (u: Unit) => {
    if (selectedUnit === u.unitCode) return;
    selectUnit(u.unitCode);
  };

  const cancelSelection = () => selectUnit('');

  if (filteredUnits.length === 0) return null;

  return (
    <div className="step-container">
      <div className="flex items-center justify-between mb-4">
        <button
          type="button"
          onClick={() => navigate('/goal')}
          className="btn-ghost !px-3 !py-1.5 text-sm"
        >
          {isAr ? <ArrowRight className="w-4 h-4" /> : <ArrowLeft className="w-4 h-4" />}
          {t('common.back')}
        </button>
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center gap-2 text-xs text-navy-400">
            <span className="w-1.5 h-1.5 rounded-full bg-gold" />
            {t('match.timeBudget')}
            <span className="num-en">· 8 {t('common.minute')}</span>
          </span>
          <NewCustomerButton />
        </div>
      </div>

      <Breadcrumb t={t} isAr={isAr} />

      <header className="text-center mb-8">
        <h1 className="text-3xl md:text-4xl font-bold text-navy mb-2">
          {t('match.title')}{' '}
          <span className="num-en text-gold-700">{filteredUnits.length}</span>{' '}
          {t('match.titleSuffix')}
        </h1>
        <p className="text-navy-600">{t('match.subtitle')}</p>
      </header>

      <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredUnits.map((u) => (
          <UnitCard
            key={u.unitCode}
            unit={u}
            selected={selectedUnit === u.unitCode}
            variant={selectedUnit === u.unitCode ? 'expanded' : 'compact'}
            onSelect={handleSelect}
          />
        ))}
      </section>

      {selectedUnitObject && (
        <div ref={configureRef} className="mt-8">
          <ConfigurePanel
            key={selectedUnitObject.unitCode}
            unit={selectedUnitObject}
            onContinue={() => navigate('/guarantees')}
            onCancel={cancelSelection}
          />
        </div>
      )}
    </div>
  );
}

function Breadcrumb({ t, isAr }: { t: ReturnType<typeof useT>; isAr: boolean }) {
  const sep = isAr ? '←' : '→';
  return (
    <nav
      aria-label="breadcrumb"
      className="flex items-center justify-center gap-2 text-xs text-navy-400 mb-6"
    >
      <span>{t('match.crumbWelcome')}</span>
      <span>{sep}</span>
      <span>{t('match.crumbProject')}</span>
      <span>{sep}</span>
      <span>{t('match.crumbGoal')}</span>
      <span>{sep}</span>
      <span className="text-navy-700 font-semibold">{t('match.crumbMatch')}</span>
    </nav>
  );
}
